import { useState, useRef, useEffect, useCallback } from 'react';
import { X, Camera, Save, Loader2, PenTool, RotateCcw, Trash2, Send, CheckCircle2, ExternalLink } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { compressImage } from '../lib/imageCompression';
import { useSignedUrl } from '../lib/storageUrl';
import ModalPortal from './ModalPortal';
import MfaSection from './settings/MfaSection';

interface ProfileModalProps {
  onClose: () => void;
}

function SignatureCanvas({ onSave, existingUrl }: { onSave: (dataUrl: string) => void; existingUrl: string | null }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasStrokes, setHasStrokes] = useState(false);
  // In `profiles.signature_url` staat een kaal pad in de documents-bucket.
  const signatureUrl = useSignedUrl('documents', existingUrl);

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * 2;
    canvas.height = rect.height * 2;
    ctx.scale(2, 2);
    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  useEffect(() => {
    initCanvas();
  }, [initCanvas]);

  const getPos = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    if ('touches' in e) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    setIsDrawing(true);
    setHasStrokes(true);
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    if ('touches' in e) e.preventDefault();
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = getPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => setIsDrawing(false);

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasStrokes(false);
  };

  const save = () => {
    const canvas = canvasRef.current;
    if (!canvas || !hasStrokes) return;
    onSave(canvas.toDataURL('image/png'));
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm font-medium text-foreground-secondary">
          <PenTool className="w-4 h-4" />
          Handtekening
        </label>
        {existingUrl && !hasStrokes && (
          <span className="text-xs text-green-600 font-medium">Opgeslagen</span>
        )}
      </div>

      {existingUrl && !hasStrokes ? (
        <div className="border-2 border-theme-border rounded-lg p-3 bg-surface-secondary">
          <img
            src={signatureUrl ?? undefined}
            alt="Handtekening"
            className="h-20 mx-auto object-contain"
          />
          <div className="flex justify-center gap-2 mt-3">
            <button
              type="button"
              onClick={() => setHasStrokes(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-foreground-secondary border border-theme-border-strong rounded-lg hover:bg-surface-tertiary transition-colors"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Opnieuw tekenen
            </button>
          </div>
        </div>
      ) : (
        <>
          <p className="text-xs text-foreground-tertiary">
            Teken uw handtekening hieronder. Deze wordt automatisch op gegenereerde documenten geplaatst.
          </p>
          <canvas
            ref={canvasRef}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
            className="w-full h-32 border-2 border-dashed border-theme-border-strong rounded-lg cursor-crosshair touch-none bg-surface hover:border-theme-border-strong transition-colors"
          />
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={clear}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-foreground-secondary hover:text-foreground transition-colors"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Wissen
            </button>
            <button
              type="button"
              onClick={save}
              disabled={!hasStrokes}
              className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium text-white bg-[#F598AF] rounded-lg hover:bg-[#E77B9B] transition-colors disabled:bg-surface-tertiary disabled:cursor-not-allowed"
            >
              <Save className="w-3.5 h-3.5" />
              Handtekening opslaan
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default function ProfileModal({ onClose }: ProfileModalProps) {
  const { profile, refreshProfile } = useAuth();
  const [fullName, setFullName] = useState(profile?.full_name || '');
  const [functionTitle, setFunctionTitle] = useState(profile?.function_title || '');
  const [phone, setPhone] = useState(profile?.phone || '');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [savingSignature, setSavingSignature] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // In `profiles.avatar_url` staat een absolute publieke storage-URL; die wordt opnieuw getekend.
  const avatarUrl = useSignedUrl(profile?.avatar_url);

  const [telegramLinked, setTelegramLinked] = useState(false);
  const [telegramLinkCode, setTelegramLinkCode] = useState<string | null>(null);
  const [telegramLinking, setTelegramLinking] = useState(false);
  const telegramPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (profile?.id) {
      supabase
        .from('profiles')
        .select('telegram_chat_id')
        .eq('id', profile.id)
        .maybeSingle()
        .then(({ data }) => {
          setTelegramLinked(!!data?.telegram_chat_id);
        });
    }
    return () => {
      if (telegramPollRef.current) clearInterval(telegramPollRef.current);
    };
  }, [profile?.id]);

  const startTelegramLink = async () => {
    if (!profile?.id) return;
    setTelegramLinking(true);

    const code = `PRACHT-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;
    const expiresAt = new Date(Date.now() + 10 * 60 * 1000).toISOString();

    await supabase.from('telegram_link_codes').delete().eq('user_id', profile.id);
    const { error: insertErr } = await supabase.from('telegram_link_codes').insert({
      user_id: profile.id,
      code,
      expires_at: expiresAt,
    });

    if (insertErr) {
      setError('Kon koppelcode niet aanmaken');
      setTelegramLinking(false);
      return;
    }

    setTelegramLinkCode(code);

    telegramPollRef.current = setInterval(async () => {
      const { data } = await supabase
        .from('profiles')
        .select('telegram_chat_id')
        .eq('id', profile.id)
        .maybeSingle();

      if (data?.telegram_chat_id) {
        setTelegramLinked(true);
        setTelegramLinkCode(null);
        setTelegramLinking(false);
        if (telegramPollRef.current) clearInterval(telegramPollRef.current);
      }
    }, 3000);
  };

  const handleSave = async () => {
    if (!profile) return;
    setSaving(true);
    setError(null);

    try {
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          full_name: fullName.trim() || null,
          function_title: functionTitle.trim() || null,
          phone: phone.trim() || null,
        })
        .eq('id', profile.id);

      if (updateError) throw updateError;
      await refreshProfile();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0] || !profile) return;
    const file = e.target.files[0];
    e.target.value = '';

    if (!file.type.startsWith('image/')) {
      setError('Selecteer een afbeelding (JPG, PNG, etc.)');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Afbeelding mag maximaal 5MB zijn');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const ext = file.name.split('.').pop();
      const storagePath = `avatars/${profile.id}.${ext}`;

      const fileToUpload = await compressImage(file);
      const { error: uploadError } = await supabase.storage
        .from('photos')
        .upload(storagePath, fileToUpload, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from('photos')
        .getPublicUrl(storagePath);

      const avatarUrl = `${urlData.publicUrl}?t=${Date.now()}`;

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: avatarUrl })
        .eq('id', profile.id);

      if (updateError) throw updateError;
      await refreshProfile();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Upload mislukt');
    } finally {
      setUploading(false);
    }
  };

  const handleSignatureSave = async (dataUrl: string) => {
    if (!profile) return;
    setSavingSignature(true);
    setError(null);

    try {
      const base64 = dataUrl.replace('data:image/png;base64,', '');
      const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
      const storagePath = `signatures/${profile.id}.png`;

      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(storagePath, bytes, {
          contentType: 'image/png',
          upsert: true,
        });

      if (uploadError) throw uploadError;

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ signature_url: storagePath })
        .eq('id', profile.id);

      if (updateError) throw updateError;
      await refreshProfile();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Handtekening opslaan mislukt');
    } finally {
      setSavingSignature(false);
    }
  };

  const handleSignatureDelete = async () => {
    if (!profile) return;
    setError(null);

    try {
      if (profile.signature_url) {
        await supabase.storage.from('documents').remove([profile.signature_url]);
      }

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ signature_url: null })
        .eq('id', profile.id);

      if (updateError) throw updateError;
      await refreshProfile();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Verwijderen mislukt');
    }
  };

  const initials = (profile?.full_name || profile?.email || '?')
    .split(' ')
    .map(w => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <ModalPortal>
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-start justify-center z-50 sm:pt-20 px-0 sm:px-4" onClick={onClose}>
      <div
        className="bg-surface rounded-t-xl sm:rounded-xl shadow-2xl w-full max-w-md animate-slide-up-sheet sm:animate-fade-in max-h-[90vh] sm:max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-theme-border shrink-0">
          <h2 className="text-lg font-semibold text-foreground">Mijn profiel</h2>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-surface-tertiary rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-foreground-secondary" />
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-6 overflow-y-auto flex-1">
          <div className="flex flex-col items-center">
            <div className="relative group">
              {profile?.avatar_url ? (
                <img
                  src={avatarUrl ?? profile.avatar_url}
                  alt={profile.full_name || 'Profiel'}
                  className="w-24 h-24 rounded-full object-cover ring-4 ring-theme-border"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-dark flex items-center justify-center ring-4 ring-theme-border">
                  <span className="text-2xl font-semibold text-white">{initials}</span>
                </div>
              )}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/40 flex items-center justify-center transition-colors cursor-pointer"
              >
                {uploading ? (
                  <Loader2 className="w-6 h-6 text-white animate-spin" />
                ) : (
                  <Camera className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
              />
            </div>
            <p className="text-xs text-foreground-tertiary mt-2">Klik op de foto om te wijzigen</p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground-secondary mb-1">Naam</label>
              <input
                type="text"
                value={fullName}
                onChange={e => setFullName(e.target.value)}
                placeholder="Volledige naam"
                className="w-full px-3 py-2.5 border border-theme-border rounded-lg focus:ring-2 focus:ring-[#F598AF] focus:border-transparent outline-none transition-all text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground-secondary mb-1">Functietitel</label>
              <input
                type="text"
                value={functionTitle}
                onChange={e => setFunctionTitle(e.target.value)}
                placeholder="Bijv. Algemeen Directeur"
                className="w-full px-3 py-2.5 border border-theme-border rounded-lg focus:ring-2 focus:ring-[#F598AF] focus:border-transparent outline-none transition-all text-sm"
              />
              <p className="text-xs text-foreground-tertiary mt-1">Wordt getoond in je e-mailhandtekening</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground-secondary mb-1">Telefoonnummer</label>
              <input
                type="tel"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                placeholder="Bijv. 0251 - 745 604"
                className="w-full px-3 py-2.5 border border-theme-border rounded-lg focus:ring-2 focus:ring-[#F598AF] focus:border-transparent outline-none transition-all text-sm"
              />
              <p className="text-xs text-foreground-tertiary mt-1">Wordt getoond in je e-mailhandtekening</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground-secondary mb-1">E-mail</label>
              <input
                type="text"
                value={profile?.email || ''}
                disabled
                className="w-full px-3 py-2.5 border border-theme-border rounded-lg bg-surface-secondary text-foreground-tertiary text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground-secondary mb-1">Rol</label>
              <input
                type="text"
                value={profile?.role || ''}
                disabled
                className="w-full px-3 py-2.5 border border-theme-border rounded-lg bg-surface-secondary text-foreground-tertiary text-sm"
              />
            </div>
          </div>

          <div className="border-t border-theme-border pt-5">
            {savingSignature ? (
              <div className="flex items-center justify-center gap-2 py-8 text-foreground-tertiary">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-sm">Handtekening opslaan...</span>
              </div>
            ) : (
              <SignatureCanvas
                onSave={handleSignatureSave}
                existingUrl={profile?.signature_url || null}
              />
            )}
            {profile?.signature_url && (
              <div className="mt-2 flex justify-end">
                <button
                  type="button"
                  onClick={handleSignatureDelete}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Handtekening verwijderen
                </button>
              </div>
            )}
          </div>

          {/* Telegram koppeling */}
          <div className="border-t border-theme-border pt-5">
            <div className="flex items-center gap-2 mb-3">
              <Send className="w-4 h-4 text-[#0088cc]" />
              <h3 className="text-sm font-medium text-foreground">Telegram Meldingen</h3>
            </div>

            {telegramLinked ? (
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                <p className="text-sm text-green-800">Telegram is gekoppeld. Je ontvangt hier meldingen.</p>
              </div>
            ) : telegramLinkCode ? (
              <div className="space-y-3">
                <p className="text-xs text-foreground-secondary">
                  Klik op de knop hieronder om de bot te openen in Telegram. Je account wordt automatisch gekoppeld.
                </p>
                <a
                  href={`https://t.me/PrachtNotificationBot?start=${telegramLinkCode}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-[#0088cc] text-white rounded-lg hover:bg-[#006699] transition-colors text-sm font-medium"
                >
                  <ExternalLink className="w-4 h-4" />
                  Open in Telegram
                </a>
                <p className="text-xs text-foreground-tertiary text-center">
                  Wacht even... de koppeling wordt automatisch gedetecteerd.
                </p>
                <div className="flex justify-center">
                  <Loader2 className="w-4 h-4 animate-spin text-[#0088cc]" />
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-foreground-secondary">
                  Ontvang je werk-meldingen direct op je telefoon via Telegram.
                </p>
                <button
                  onClick={startTelegramLink}
                  disabled={telegramLinking}
                  className="flex items-center gap-2 px-4 py-2 bg-[#0088cc] text-white rounded-lg hover:bg-[#006699] transition-colors text-sm font-medium disabled:opacity-50"
                >
                  <Send className="w-4 h-4" />
                  Koppel Telegram
                </button>
              </div>
            )}
          </div>

          {/* Twee-factor-authenticatie */}
          <MfaSection />

          {error && (
            <div className="p-3 bg-red-50 border border-red-100 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-4 sm:px-6 py-4 border-t border-theme-border bg-surface-secondary rounded-b-xl shrink-0 safe-bottom">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-foreground-secondary hover:bg-surface-tertiary rounded-lg transition-colors"
          >
            Annuleren
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-dark hover:bg-dark-secondary rounded-lg transition-colors disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Opslaan
          </button>
        </div>
      </div>
    </div>
    </ModalPortal>
  );
}
